import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter, Loader2, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { handleFormSubmit } from '@/utils/handleFormSubmit';

// API base URL - uses relative path for monolithic deployment
const API_BASE_URL = '/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubscribing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubscribed(true);
        await handleFormSubmit({ email }, 'newsletter_signup');
        toast({ title: '✅ Subscribed!', description: data.message || 'Thank you for subscribing!' });
        setEmail('');
      } else {
        throw new Error(data.error || 'Failed to subscribe');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to subscribe. Please try again.';
      toast({ title: '❌ Error', description: message, variant: 'destructive' });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
  <footer className="bg-secondary text-secondary-foreground" role="contentinfo">
    <div className="container mx-auto px-4 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand */}
        <div>
          <img src="/images/logohori.JPG" alt="OyinBatfo Branding Solutions" className="h-12 mb-4 rounded" loading="lazy" />
          <p className="text-secondary-foreground/60 text-sm leading-relaxed">
            Empowering brands, inspiring futures. Premium branding solutions for schools and corporates since 2012.
          </p>
          <div className="flex gap-3 mt-6">
            {[
              { Icon: Instagram, label: 'Instagram', href: '#' },
              { Icon: Facebook, label: 'Facebook', href: '#' },
              { Icon: Twitter, label: 'Twitter / X', href: '#' },
            ].map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                aria-label={`Follow us on ${label}`}
                className="w-10 h-10 rounded-lg bg-secondary-foreground/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-bold font-display mb-4 text-secondary-foreground">Services</h4>
          <ul className="space-y-2.5 text-sm text-secondary-foreground/60">
            {['DTG Printing', 'Sublimation', '3D Sublimation', 'Monogram & Embroidery', 'Mug Press', 'Rhinestone Design'].map(s => (
              <li key={s}>
                <a href="#services" className="hover:text-primary transition-colors">{s}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-bold font-display mb-4 text-secondary-foreground">Quick Links</h4>
          <ul className="space-y-2.5 text-sm text-secondary-foreground/60">
            {[
              { label: 'Home', href: '#home' },
              { label: 'About Us', href: '#about' },
              { label: 'For Schools', href: '#schools' },
              { label: 'Gallery', href: '#gallery' },
              { label: 'Blog', href: '/blog' },
              { label: 'Contact', href: '#contact' },
            ].map(l => (
              <li key={l.label}>
                <a href={l.href} className="hover:text-primary transition-colors">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter */}
        <div>
          <h4 className="font-bold font-display mb-4 text-secondary-foreground">Stay Updated</h4>
          <p className="text-sm text-secondary-foreground/60 mb-4">Subscribe for branding tips, special offers, and industry insights.</p>
          <form className="flex gap-2" onSubmit={handleSubscribe}>
            <Input
              placeholder="Your email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubscribing || isSubscribed}
              aria-label="Email address for newsletter"
              className="bg-secondary-foreground/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/40"
            />
            <Button 
              type="submit"
              disabled={isSubscribing || isSubscribed}
              className="bg-primary text-primary-foreground hover:bg-primary-glow flex-shrink-0"
            >
              {isSubscribing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isSubscribed ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                'Join'
              )}
            </Button>
          </form>
          <div className="mt-6 space-y-2.5 text-sm text-secondary-foreground/60">
            <a href="tel:+2348050488734" className="flex items-center gap-2 hover:text-primary transition-colors"><Phone className="w-4 h-4" /> +234 805 048 8734</a>
            <a href="mailto:info@oyinbatfo.com" className="flex items-center gap-2 hover:text-primary transition-colors"><Mail className="w-4 h-4" /> info@oyinbatfo.com</a>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Lagos & Ilorin, Nigeria</div>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-secondary-foreground/10">
      <div className="container mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-secondary-foreground/40">
        <p>© 2012 – {new Date().getFullYear()} OyinBatfo Fashion/Branding Solutions. All rights reserved.</p>
        <div className="flex gap-6">
          <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);
};

export default Footer;
